import { Button, Flex, Spinner, Stack, Td, Text } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  CyclePhase,
  useGetProcurementPhaseListQuery,
  type CyclePhaseDto,
  type ProcurementOverviewDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { ListLoadError } from "../../common/ListLoadError";
import { formatNumber } from "../../common/formatting/formatNumber";
import { WIDE_ONLY_CELL } from "../../common/responsiveTable";
import { urls } from "../../urls";
import { PanelRow, PanelTable, TitleCell } from "../home/panels/PanelTable";
import { OverviewBar } from "./OverviewBar";
import type { PeriodRange } from "./procurementFigures";

export const parseCyclePhase = (value: string | null): CyclePhase | null =>
  Object.values(CyclePhase).find((phase) => phase === value) ?? null;

export const cyclePhaseLabel = (t: TFunction, phase: CyclePhase): string =>
  ({
    [CyclePhase.Bidding]: t("Published → bid deadline"),
    [CyclePhase.Evaluation]: t("Bid deadline → evaluated"),
    [CyclePhase.Award]: t("Evaluated → awarded"),
    [CyclePhase.Contract]: t("Awarded → contract concluded"),
  })[phase];

const days = (t: TFunction, value: number): string => t("{{count}} days", { count: Math.round(value) });

export const CycleTimePerPhase = ({
  overview,
  phaseLink,
}: {
  overview: ProcurementOverviewDto;
  phaseLink: (phase: CyclePhase) => string;
}) => {
  const { t } = useTranslation();
  const phases = overview.phases;
  const longest = Math.max(1, ...phases.map((phase) => phase.medianDays ?? 0));
  const nothingLeftAPhase = phases.every((phase) => phase.procurements === 0);
  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="cycle-time-per-phase"
    >
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("Cycle time per phase")}
      </Text>
      {nothingLeftAPhase ? (
        <Text fontSize="sm" color="smMuted" data-testid="no-phase-left">
          {t("No procurement left a phase in the period.")}
        </Text>
      ) : (
      <Stack spacing="1">
        {phases.map((phase) => (
          <OverviewBar
            key={phase.phase}
            label={cyclePhaseLabel(t, phase.phase)}
            width={phase.medianDays == null ? null : (phase.medianDays / longest) * 100}
            shown={
              phase.procurements === 0
                ? String(t("None in the period"))
                : phase.medianDays == null
                  ? String(
                      t("{{count}} procurements · Too few for a median", {
                        count: phase.procurements,
                      }),
                    )
                  : days(t, phase.medianDays)
            }
            details={phaseDetails(t, phase)}
            link={phase.procurements > 0 ? phaseLink(phase.phase) : null}
            testId={`phase-${phase.phase}`}
          />
        ))}
      </Stack>
      )}
      <Text fontSize="sm" color="smMuted">
        {t(
          "Median days in each phase, for the procurements that left the phase in the period. A procurement still in a phase is not counted in it.",
        )}
      </Text>
    </Stack>
  );
};

const phaseDetails = (t: TFunction, phase: CyclePhaseDto): string[] =>
  phase.procurements === 0
    ? [String(t("No procurement left this phase in the period."))]
    : [
        phase.medianDays == null
          ? String(t("Too few for a median"))
          : String(t("Median: {{days}}", { days: days(t, phase.medianDays) })),
        String(
          t("{{count}} procurements left this phase in the period", {
            count: phase.procurements,
          }),
        ),
        ...(phase.slowestDays == null ? [] : [String(t("Slowest procurement: {{days}}", { days: days(t, phase.slowestDays) }))]),
      ];

export const PhaseProcurements = ({
  phase,
  range,
  closeLink,
}: {
  phase: CyclePhase;
  range: PeriodRange;
  closeLink: string;
}) => {
  const { t } = useTranslation();
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const { currentData: data, isFetching, error, refetch } = useGetProcurementPhaseListQuery({
    orgId: organizationId,
    phase,
    from: range.from,
    to: range.to,
  });
  const { message } = useApiError();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.scrollIntoView?.({ block: "nearest" });
    heading.current?.focus();
  }, [phase]);

  return (
    <Stack
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="phase-procurements"
    >
      <Flex justify="space-between" align="baseline" gap="3" wrap="wrap">
        <Text as="h2" ref={heading} tabIndex={-1} fontWeight="semibold" color="smText" _focus={{ outline: "none" }}>
          {t("{{phase}}: the procurements in the phase", {
            phase: cyclePhaseLabel(t, phase),
          })}
        </Text>
        <Button as={RouterLink} to={closeLink} size="sm" variant="link" colorScheme="teal">
          {t("Close")}
        </Button>
      </Flex>
      {error ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : !data ? (
        <Spinner color="smPrimary" data-testid="phase-procurements-loading" />
      ) : data.slowestFirst.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No procurement left this phase in the period.")}
        </Text>
      ) : (
        <>
          <Text fontSize="sm" color="smMuted">
            {data.procurements > data.slowestFirst.length
              ? t("The {{shown}} with the most days in the phase, of {{count}} procurements.", {
                  shown: formatNumber(data.slowestFirst.length),
                  count: data.procurements,
                })
              : t("{{count}} procurements, the most days in the phase first.", {
                  count: data.procurements,
                })}
          </Text>
          <Flex overflowX="auto">
            <PanelTable headers={[t("Procurement"), t("Days in the phase")].map(String)} phoneColumns={1}>
              {data.slowestFirst.map((row) => (
                <PanelRow key={row.id} to={urls.app.sourcingEvents.view.go(row.id)}>
                  <TitleCell
                    title={row.title || String(t("Untitled"))}
                    subtitle={row.referenceId ?? undefined}
                    wrapTitle
                    phoneDetail={
                      <Text fontSize="xs" color="smMuted">
                        {days(t, row.days)}
                      </Text>
                    }
                  />
                  <Td isNumeric display={WIDE_ONLY_CELL}>
                    {formatNumber(Math.round(row.days))}
                  </Td>
                </PanelRow>
              ))}
            </PanelTable>
          </Flex>
        </>
      )}
    </Stack>
  );
};
